<template>
  <flex-column class="wrapper">
    <flex-column v-if="loading"> Loading </flex-column>
    <template v-else>
      <template v-if="definition">
        <flex-header>
          <div class="header">
            <flex-row vcenter gap>
              <flex-cell shrink>
                <h3>{{ modalTitle }}</h3>
              </flex-cell>
              <flex-cell v-if="maximum">
                <span class="font-muted font-sm"
                  >({{ model.length }} of {{ maximum }})</span
                >
              </flex-cell>
              <flex-cell>
                <template v-if="!showFilters">
                  <search
                    autofocus
                    v-model="search"
                    :loading="searching"
                    :debounce="500"
                    placeholder="Keyword Search"
                  />
                </template>
              </flex-cell>
              <flex-cell shrink v-if="dateFilterEnabled && !showFilters">
                <ux-menu :closeOnClick="false">
                  <template #activator="{ on }">
                    <ux-button
                      v-on="on"
                      v-tippy="{
                        placement: 'bottom',
                        content: `Modify the visible date range`,
                      }"
                    >
                      {{ dateSummary }}
                    </ux-button>
                  </template>
                  <div class="date-range">
                    <flex-row gap>
                      <flex-cell shrink>
                        <ul class="date-shortcuts">
                          <li v-for="shortcut in dateShortcuts">
                            <a @click="setDateRange(shortcut)">{{
                              shortcut.title
                            }}</a>
                          </li>
                        </ul>
                      </flex-cell>
                      <flex-cell>
                        <date-range v-model="dateRange" />
                      </flex-cell>
                    </flex-row>
                  </div>
                </ux-menu>
              </flex-cell>

              <flex-cell shrink>
                <ux-button
                  :icon="$device.breakpoint.mobile"
                  @click="showFilters = !showFilters"
                >
                  <template v-if="$device.breakpoint.mobile">
                    <ux-icon icon="fa-search"
                  /></template>
                  <template v-else
                    >{{ showFilters ? "Hide" : "Show" }} Filters</template
                  >
                </ux-button>
              </flex-cell>
              <flex-cell shrink>
                <ux-button color="primary" @click="selectionComplete"
                  >Done</ux-button
                >
              </flex-cell>
            </flex-row>
          </div>
        </flex-header>
        <content-browser
          :dateFilterEnabled="dateFilterEnabled"
          v-model:rolodexPrimary="rolodexPrimary"
          :showFilters="showFilters"
          ref="browser"
          :search="search"
          @click:row="rowClicked"
          :maximum="options.maximum"
          v-model:dateRange="dateRange"
          v-model="model"
          :type="options.type"
          :options="browserOptions"
        >
          <template #abovecontent>
            <flex-header v-if="rolodexEnabled">
              <div class="rolodex">
                <ux-button
                  size="sm"
                  @click="toggleRolodex('')"
                  icon
                  :color="!rolodexPrimary ? 'primary' : ''"
                  >All</ux-button
                >
                <ux-button
                  size="sm"
                  @click="toggleRolodex(letter)"
                  icon
                  :color="rolodexPrimary === letter ? 'primary' : ''"
                  v-for="letter in letters"
                  >{{ letter }}</ux-button
                >
              </div>
            </flex-header>
          </template>
        </content-browser>
      </template>
    </template>
  </flex-column>
</template>
<script>
import Search from "../form/inputs/search.vue";

import ContentBrowser from "../content/browser.vue";
import ModalMixin from "./ModalMixin";
import isDateFilterEnabledType from "./isDateFilterEnabledType.js";
import { DateTime } from "luxon";
import DateRange from "../ui/daterange.vue";
import getDateShortcuts from "./dateShortcuts.js";

export default {
  components: {
    ContentBrowser,
    Search,
    DateRange,
  },
  mixins: [ModalMixin],
  async created() {
    var self = this;
    await self.$glossary.ensure(self.type);

    self.loading = false;
    var definition = self.$glossary.hash[self.type];

    if (!definition) {
      //Close immediately
      self.$sdk.notify(
        "You do not have the required permissions to list content of this type",
      );
      self.dismiss();
      return;
    }

    self.definition = definition;

    if (self.bigData && self.rolodexEnabled) {
      self.rolodexLetter = "A";
    }
  },
  computed: {
    modalTitle() {
      if (this.options.title) {
        return this.options.title;
      } else {
        return `Select ${this.maximum == 1 ? this.title : this.plural}`;
      }
    },
    dateShortcuts() {
      const definedType = this.definition.key;
      const basicType = this.definition.definesType || definedType;

      return getDateShortcuts(basicType, definedType);
    },
    bigData() {
      return this.definition?.count > 20000;
    },
    dateFilterEnabled() {
      const definedType = this.definition.key;
      const basicType = this.definition.definesType || definedType;

      return isDateFilterEnabledType(basicType, definedType);
    },

    dateSummary() {
      let { startDate, endDate } = this.dateRange;

      if (!startDate || String(startDate) == "Invalid Date") {
        return "Invalid date";
      }

      if (!endDate || String(endDate) == "Invalid Date") {
        return "Invalid date";
      }

      if (
        new Date(startDate).toISOString() == new Date(endDate).toISOString()
      ) {
        return `${DateTime.fromJSDate(startDate).toFormat("dd LLL yyyy")}`;
      } else {
        const startMonth = DateTime.fromJSDate(startDate).toFormat("LLL yyyy");
        const endMonth = DateTime.fromJSDate(endDate).toFormat("LLL yyyy");

        const startYear = DateTime.fromJSDate(startDate).toFormat("yyyy");
        const endYear = DateTime.fromJSDate(endDate).toFormat("yyyy");

        const startDay = DateTime.fromJSDate(startDate).toFormat("dd");
        const endDay = DateTime.fromJSDate(endDate).toFormat("dd");
        if (startMonth == endMonth) {
          if (startDay === endDay) {
            return `${DateTime.fromJSDate(startDate).toFormat("d LLL yyyy")}`;
          } else {
            return `${DateTime.fromJSDate(startDate).toFormat("d")} - ${DateTime.fromJSDate(endDate).toFormat("d LLL yyyy")}`;
          }
        }

        if (startYear == endYear) {
          return `${DateTime.fromJSDate(startDate).toFormat("d LLL")} - ${DateTime.fromJSDate(endDate).toFormat("d LLL yyyy")}`;
        }

        return `${DateTime.fromJSDate(startDate).toFormat("d LLL yyyy")} - ${DateTime.fromJSDate(endDate).toFormat("d LLL yyyy")}`;
      }
    },
    dateRangeField() {
      return {
        key: "dates",
        type: "object",
        widget: "daterange",
        minimum: 0,
        maximum: 1,
      };
    },
    rolodexEnabled() {
      let enabled;

      if (!this.bigData) {
        return;
      }

      const basicType = this.definition.definesType || this.definition.key;
      switch (basicType) {
        case "profile":
        case "persona":
          enabled = true;
          break;
      }

      if (this.search || this.shortcut) {
        enabled = false;
      }

      return enabled;
    },
    rolodexPrimary: {
      get() {
        if (this.search || this.shortcut) {
          return;
        }

        return this.rolodexLetter;
      },
      set(r) {
        if (this.search || this.shortcut) {
          return;
        }

        this.rolodexLetter = r;
      },
    },
    letters() {
      return "abcdefghijklmnopqrstuvwxyz".toUpperCase().split("");
    },
    type() {
      return this.options.type;
    },
    title() {
      return this.definition.title;
    },
    plural() {
      return this.definition.plural;
    },
    maximum() {
      return this.options.maximum || 0;
    },
    browserOptions() {
      return this.options.browserOptions || {};
    },
  },
  watch: {
    search(s, before) {
      const self = this;

      if (!s && self.bigData) {
        if (!self.rolodexLetter) {
          self.rolodexLetter = "A";
        }
      }
    },
  },
  data() {
    return {
      showFilters: false,
      search: "",
      searching: false,
      definition: null,
      loading: true,
      model: this.options.model.slice(),
      rolodexLetter: "",
      dateRange: {
        startDate: DateTime.now().startOf("day").toJSDate(),
        endDate: DateTime.now().plus({ months: 3 }).toJSDate(),
      },
    };
  },
  methods: {
    setDateRange({ startDate, endDate }) {
      const validStartDate = startDate && !isNaN(new Date(startDate).getTime());
      const validEndDate = endDate && !isNaN(new Date(endDate).getTime());

      if (validStartDate && validEndDate) {
        this.dateRange.startDate = new Date(startDate);
        this.dateRange.endDate = new Date(endDate);
      } else {
        console.log("WARNING: Invalid date range selected", {
          startDate,
          endDate,
          validStartDate,
          validEndDate,
        });
      }
    },
    toggleRolodex(letter) {
      if (this.rolodexLetter === letter) {
        this.rolodexLetter = "";
      } else {
        this.rolodexLetter = letter;
      }
    },
    rowClicked(row) {
      this.$refs.browser.toggle(row);
    },
    selectionComplete(data) {
      this.close(this.model);
    },
  },
};
</script>
<style lang="scss" scoped>
.wrapper {
  min-width: 80vw;
  min-height: 80vh;
  border-radius: 0.5em;
  overflow: hidden;
}

.header {
  background: var(--bg1, #fff);
  padding: 1em;
  border-bottom: 1px solid rgba(#000, 0.1);

  h1,
  h2,
  h3,
  h4,
  h5 {
    margin: 0;
  }
}

.date-range {
  padding: 1em;
  background: var(--bg1, #fff);

  .date-shortcuts {
    padding: 0;
    margin: 0;
    list-style-type: none;
    width: 200px;

    li {
      display: block;

      a {
        cursor: pointer;
        display: block;
        padding: 0.5em 1em;

        &:hover {
          color: var(--primary);
        }
      }
    }
  }
}

.rolodex {
  padding: 0.5em;
  white-space: nowrap;
  overflow-x: auto;
  border-bottom: 1px solid rgba(#000, 0.1);
  text-align: center;

  .ux-btn {
    font-weight: 500;
  }

  // a {
  //     display: inline-block;
  //     padding:0.5em;
  //     text-align: center;
  // }
}
</style>
